import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { uploadData, getUrl } from 'aws-amplify/storage'
import PageModal from '../../common/PageModal'
import SimpleButton from '../../common/SimpleButton'
import LoadingSpinner from '../../common/LoadingSpinner'
import { toast } from '../../common/Toast'
import { UploadCloudFull, TrashCanFull } from '../../../icons'
import { getFileIcon } from '../../../utils/functions'
import { sparepartsUploadWritePermission } from '../../../utils/permissionValidation'
import { sparepartsUploadSampleTemplate } from '../../../utils/fixedUrls'
import '../uploads.scss'

const ACCEPTED_FILE_TYPES = ['application/vnd.ms-excel.template.macroEnabled.12']
const ACCEPTED_FILE_EXTENSIONS = ['.xltm']
const MAX_FILE_SIZE = 5 * 1024 * 1024

const BulkUploadSparepartsModal = ({ closeModal }) => {
  const { t } = useTranslation()
  const fileInputRef = useRef(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [isUploading, setIsUploading] = useState(false)

  const handleDrop = (event) => {
    event.preventDefault()
    const { files } = event.dataTransfer

    if (files && files.length > 0) {
      validateAndSetFile(files[0])
    }
  }

  const handleFileSelect = (event) => {
    const { files } = event.target

    if (files && files.length > 0) {
      validateAndSetFile(files[0])
    }

    if (event.target) {
      event.target.value = ''
    }
  }

  const validateAndSetFile = (file) => {
    const hasAcceptedExtension = ACCEPTED_FILE_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext))
    if (!ACCEPTED_FILE_TYPES.includes(file.type) && !hasAcceptedExtension) {
      toast.error(t('spareparts_upload_context.file_type_error'))
      return
    }

    if (file.size > MAX_FILE_SIZE) {
      toast.error(t('spareparts_upload_context.file_size_error'))
      return
    }

    if (selectedFile) {
      toast.error(t('spareparts_upload_context.only_one_file_allowed'))
      return
    }

    setSelectedFile(file)
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
  }

  const handleDownloadTemplate = async () => {
    try {
      const { url } = await getUrl({
        path: sparepartsUploadSampleTemplate,
        options: {
          accessLevel: 'public',
        },
      })
      window.open(url.toString(), '_blank')
    } catch (error) {
      console.error('Error downloading template:', error)
      toast.error(t('spareparts_upload_context.template_download_error'))
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error(t('spareparts_upload_context.please_select_file'))
      return
    }

    try {
      setIsUploading(true)
      const timestamp = Date.now()
      const fileName = `${timestamp}-${selectedFile.name}`
      const storagePath = `spare-parts/uploads/${fileName}`

      await uploadData({
        path: storagePath,
        data: selectedFile,
        options: {
          accessLevel: 'public',
          contentType: selectedFile.type || 'application/vnd.ms-excel.template.macroEnabled.12',
        },
      }).result

      toast.success(t('spareparts_upload_context.bulk_upload_success'))
      closeModal()
    } catch (error) {
      console.error('Error uploading CSV file to S3:', error)
      toast.error(t('spareparts_upload_context.bulk_upload_error'))
    } finally {
      setIsUploading(false)
    }
  }

  const footerContent = () => {
    return (
      <>
        <SimpleButton className="cancel-button" onClick={closeModal} disabled={isUploading}>
          {t('cancel')}
        </SimpleButton>
        <SimpleButton
          className="submit-button"
          onClick={handleUpload}
          disabled={!selectedFile || isUploading}
          requiredPermission={sparepartsUploadWritePermission}
        >
          {t('upload')}
        </SimpleButton>
      </>
    )
  }

  return (
    <>
      <PageModal
        toggle
        onToggle={closeModal}
        className="uploads-modal"
        title={t('spareparts_upload_context.bulk_upload_spareparts')}
        footerContent={footerContent()}
      >
        <div className="uploads-modal-content">
          <div className="section-title">{t('spareparts_upload_context.upload_file').toUpperCase()}</div>
          <div className="information-box-text">
            <div className="information-wrapper">
              <div className="small-disclaimer">
                <p className="promotion-first">{t('spareparts_upload_context.file_format_hint_1')}</p>
                <p>{t('spareparts_upload_context.file_format_hint_2')}</p>
                <p className="normal-font">
                  {`${t('spareparts_upload_context.file_format_hint_3')} `}
                  <span>
                    <a
                      href="#!"
                      onClick={(e) => {
                        e.preventDefault()
                        handleDownloadTemplate()
                      }}
                      className="external-link"
                    >
                      {t('here')}
                    </a>
                    .
                  </span>
                </p>
              </div>
            </div>
          </div>
          <div className="row-wrapper">
            <div className="input-wrapper">
              <div className="upload-wrapper" onDrop={handleDrop} onDragOver={(e) => e.preventDefault()}>
                <label className="file-input-wrapper" htmlFor="spareparts-csv-file-input">
                  <UploadCloudFull />
                  <div>
                    <span>{t('other_context.drop_files_to_attach_or')}</span>
                    <span className="call-to-action">{` ${t('browse')}`}</span>
                  </div>
                </label>
                <input ref={fileInputRef} className="file-input" id="spareparts-csv-file-input" onChange={handleFileSelect} type="file" accept=".xltm" />
              </div>
            </div>
          </div>

          {selectedFile && (
            <>
              <div className="separator" />
              <div className="section-title">{t('spareparts_upload_context.selected_file').toUpperCase()}</div>
              <div className="row-wrapper">
                <div className="input-wrapper">
                  <div className="value">
                    {getFileIcon(selectedFile.name)}
                    <span>{selectedFile.name}</span>
                    <span className="file-size">({(selectedFile.size / 1024).toFixed(2)} KB)</span>
                    <TrashCanFull className="action-icon" style={{ marginLeft: 'auto' }} onClick={handleRemoveFile} />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </PageModal>
      {isUploading && (
        <div className="spinner-wrapper spinner-locked">
          <LoadingSpinner />
        </div>
      )}
    </>
  )
}

export default BulkUploadSparepartsModal
